import { RefObject, useEffect, useRef, useState } from 'react'
import { PacketSection, SectionKey } from './sections'

interface Options {
  sections: PacketSection[]
  scrollRootRef: RefObject<HTMLElement>
  ready?: boolean
}

const hashKey = (): string =>
  typeof window !== 'undefined' ? decodeURIComponent(window.location.hash.slice(1)) : ''

export const useActiveSection = ({
  sections,
  scrollRootRef,
  ready = true,
}: Options): SectionKey | undefined => {
  // `sections` is now the packet's merged deck, which is empty while the packet
  // loads — hence `undefined` rather than an unguarded `sections[0].key`.
  const [active, setActive] = useState<SectionKey | undefined>(() => {
    const hash = hashKey()
    return sections.find(s => s.key === hash)?.key ?? sections[0]?.key
  })
  // The merged list is a new identity whenever the packet refetches, so this
  // effect re-runs — without a latch the initial-hash scroll would re-fire and
  // yank the document back while the AE is scrolling.
  const scrolledToHashRef = useRef(false)

  useEffect(() => {
    if (!ready) return
    const root = scrollRootRef.current
    if (!root) return

    if (!scrolledToHashRef.current) {
      const initialMatch = sections.find(s => s.key === hashKey())
      if (initialMatch) {
        const el = document.getElementById(initialMatch.key)
        if (el) {
          el.scrollIntoView({ block: 'start' })
          scrolledToHashRef.current = true
        }
      } else {
        scrolledToHashRef.current = true
      }
    }

    const visible = new Map<SectionKey, number>()

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const key = (entry.target as HTMLElement).dataset.sectionKey as SectionKey | undefined
          if (!key) continue
          if (entry.isIntersecting) {
            visible.set(key, entry.boundingClientRect.top)
          } else {
            visible.delete(key)
          }
        }
        if (visible.size === 0) return
        let topKey: SectionKey | undefined
        let topVal = Infinity
        visible.forEach((v, k) => {
          if (v < topVal) {
            topVal = v
            topKey = k
          }
        })
        if (!topKey) return
        setActive(prev => (prev === topKey ? prev : topKey!))
      },
      {
        root,
        threshold: [0, 0.25, 0.5],
        rootMargin: '-10% 0px -60% 0px',
      }
    )

    for (const s of sections) {
      const el = document.getElementById(s.key)
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [sections, scrollRootRef, ready])

  return active
}
